import { AVAILABLE_LANGS } from "../constants/i18n.constants";

/**
 * Generates a mapping of language codes to their corresponding JSON loaders.
 *
 * The `loader` object is a dynamic configuration used for lazy-loading i18n resources.
 * It is created by iterating over the `AVAILABLE_LANGS` array, which contains a list of supported language codes.
 *
 * For each language code:
 * - An entry is added to the `loader` object.
 * - The entry's key is the language code (e.g., `'en'` for English, `'fr'` for French).
 * - The entry's value is a function that dynamically imports the corresponding language JSON file
 *   from the `../i18n/` directory (e.g., `../i18n/en.json`).
 *
 * This setup enables efficient and on-demand loading of translation files, reducing the initial bundle size
 * and improving application performance, especially for multilingual applications.
 *
 * Example:
 * If `AVAILABLE_LANGS = ['en', 'fr']`, the resulting `loader` object will be:
 * {
 *   en: () => import('../i18n/en.json'),
 *   fr: () => import('../i18n/fr.json'),
 * }
 *
 * @returns A dictionary where each key is a language code and each value is a function
 * that loads the corresponding translation file dynamically.
 */
export const i18nLoader = AVAILABLE_LANGS.reduce(
  (acc: { [key: string]: () => Promise<any> }, lang: string) => {
    acc[lang] = () => import(`../i18n/${lang}.json`);
    return acc;
  },
  {} as { [key: string]: () => Promise<any> }
);
